import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@roger/ui-components';
import { Loader2, Users } from 'lucide-react';
import type { OrgMemberResponse } from '../../clinician-manager/types';
import type { AgencyClinicResponse } from '../../clinician-manager/types';
import {
  RoleAssignmentContent,
  type RoleAssignmentHandle,
} from './RoleAssignmentDialog';
import {
  EditUserInfoSection,
  type EditUserInfoHandle,
} from './EditUserInfoSection';
import { RogerDocAccountSection } from '../../clinician-manager/RogerDocAccountSection';
import { useClinicDisciplines } from '../../clinician-manager/useClinicDisciplines';
import { EmployeeTypeSection } from '../../clinician-manager/EmployeeTypeSection';
import {
  UserTypeSection,
  type UserTypeSectionHandle,
} from '../../clinician-manager/UserTypeSection';
import { useEmployeeTypes } from '../../clinician-manager/useEmployeeTypes';
import {
  useRoleAssignmentDialog,
  RoleAssignmentSaveError,
} from './useRoleAssignmentDialog';
import {
  updateClinicDisciplines,
  updateEmployeeTypes,
} from '../../../services/api';
import { useStore } from '../../../store';
import { useShallow } from 'zustand/react/shallow';

type MemberSettingsDialogProps = {
  member: OrgMemberResponse | null;
  clinics: AgencyClinicResponse[];
  /** Kinnser-synced clinic IDs hidden from the picker; existing role assignments are preserved on save. */
  preserveRoleAssignmentsForClinicIds?: ReadonlySet<string>;
  onClose: () => void;
  onSuccess: () => void;
  /** Refresh RogerDoc link data without closing the dialog. */
  onRogerDocLinkChanged?: () => void;
};

export const MemberSettingsDialog: React.FC<MemberSettingsDialogProps> =
  React.memo(function MemberSettingsDialog({
    member,
    clinics,
    preserveRoleAssignmentsForClinicIds,
    onClose,
    onSuccess,
    onRogerDocLinkChanged,
  }) {
    const { hasPermission, showError } = useStore(
      useShallow(state => ({
        hasPermission: state.hasPermission,
        showError: state.showError,
      }))
    );
    const isSuperUser = hasPermission('admin:super-user');
    const canEditUserInfo = isSuperUser && !!member?.roger_user_id;

    const saveDisciplinesRef = useRef<() => Promise<void>>(async () => {});
    const saveEmployeeTypesRef = useRef<() => Promise<void>>(async () => {});
    const saveUserTypeRef = useRef<UserTypeSectionHandle>(null);
    const validateEmployeeTypesRef = useRef<() => void>(() => {});

    // Validation has to run before any write: roles, clinic roles and
    // disciplines are all committed by the time onAfterRoleSave fires, so
    // rejecting there would leave the user with a save that half happened.
    const onBeforeRoleSave = useCallback(async () => {
      validateEmployeeTypesRef.current();
    }, []);

    const onAfterRoleSave = useCallback(async () => {
      await saveDisciplinesRef.current();
      await saveEmployeeTypesRef.current();
      await saveUserTypeRef.current?.saveIfDirty();
    }, []);

    const roleAssignment = useRoleAssignmentDialog(
      member,
      onSuccess,
      onBeforeRoleSave,
      onAfterRoleSave,
      { preserveRoleAssignmentsForClinicIds }
    );

    const {
      rows: disciplineRows,
      isDirty: disciplinesDirty,
      toggleDiscipline,
      selectAllDisciplinesForClinic,
      clearDisciplinesForClinic,
      selectAllDisciplines,
      clearAllDisciplines,
      getAssignments: getDisciplineAssignments,
    } = useClinicDisciplines(member, {
      pendingAssignableClinicIds: roleAssignment.assignableClinicIds,
      clinics,
    });

    const clinicDisciplines = useMemo(
      () =>
        member?.roger_user_id
          ? {
              rows: disciplineRows,
              onToggleDiscipline: toggleDiscipline,
              onSelectAllDisciplines: selectAllDisciplines,
              onClearAllDisciplines: clearAllDisciplines,
              onSelectAllForClinic: selectAllDisciplinesForClinic,
              onClearForClinic: clearDisciplinesForClinic,
            }
          : undefined,
      [
        member?.roger_user_id,
        disciplineRows,
        toggleDiscipline,
        selectAllDisciplines,
        clearAllDisciplines,
        selectAllDisciplinesForClinic,
        clearDisciplinesForClinic,
      ]
    );

    useEffect(() => {
      saveDisciplinesRef.current = async () => {
        if (!disciplinesDirty || !member?.roger_user_id) return;
        try {
          await updateClinicDisciplines({
            userId: member.roger_user_id,
            assignments: getDisciplineAssignments(),
            throwOnError: true,
          });
        } catch (error) {
          console.error('Failed to save clinic disciplines:', error);
          showError('Failed to save clinic disciplines. Please try again.');
          throw new RoleAssignmentSaveError('disciplines save failed');
        }
      };
    }, [
      disciplinesDirty,
      getDisciplineAssignments,
      member?.roger_user_id,
      showError,
    ]);

    const {
      rows: employeeTypeRows,
      isDirty: employeeTypesDirty,
      invalidClinicIds: invalidEmployeeTypeClinicIds,
      setEmployeeType,
      setEmployeeTypeOther,
      getAssignments: getEmployeeTypeAssignments,
    } = useEmployeeTypes(member);

    useEffect(() => {
      validateEmployeeTypesRef.current = () => {
        if (!employeeTypesDirty || !member?.roger_user_id) return;
        if (invalidEmployeeTypeClinicIds.size > 0) {
          showError(
            'Enter an employee type for clinics where Other is selected.'
          );
          throw new RoleAssignmentSaveError('employee type validation failed');
        }
      };
    }, [
      employeeTypesDirty,
      invalidEmployeeTypeClinicIds,
      member?.roger_user_id,
      showError,
    ]);

    useEffect(() => {
      saveEmployeeTypesRef.current = async () => {
        if (!employeeTypesDirty || !member?.roger_user_id) return;
        try {
          await updateEmployeeTypes({
            userId: member.roger_user_id,
            assignments: getEmployeeTypeAssignments(),
            throwOnError: true,
          });
        } catch (error) {
          console.error('Failed to save employee types:', error);
          showError('Failed to save employee types. Please try again.');
          throw new RoleAssignmentSaveError('employee types save failed');
        }
      };
    }, [
      employeeTypesDirty,
      getEmployeeTypeAssignments,
      member?.roger_user_id,
      showError,
    ]);

    const editUserInfoRef = useRef<EditUserInfoHandle>(null);
    const roleAssignmentRef = useRef<RoleAssignmentHandle>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isRolesLoading, setIsRolesLoading] = useState(false);

    const dialogTitle = useMemo(() => {
      if (!member) return '';
      const first = member.first_name?.trim() ?? '';
      const last = member.last_name?.trim() ?? '';
      const email = member.email?.trim() ?? '';
      const hasFullName = Boolean(first && last);
      const subject = hasFullName ? `${first} ${last}` : email || 'this member';
      return `Manage ${subject}`;
    }, [member]);

    const handleRogerDocLinkChanged = useCallback(() => {
      onRogerDocLinkChanged?.();
    }, [onRogerDocLinkChanged]);

    const handleSave = useCallback(async () => {
      setIsSaving(true);
      try {
        // Validate ahead of the user-info save too, not just the roles save:
        // otherwise a name edit commits and only then does the dialog reject,
        // which is the same partial save from the other direction.
        try {
          validateEmployeeTypesRef.current();
        } catch {
          // Message already shown by the validator.
          return;
        }
        if (canEditUserInfo && editUserInfoRef.current) {
          try {
            await editUserInfoRef.current.saveIfDirty();
          } catch {
            // Error already shown by EditUserInfoSection; stop here
            return;
          }
        }
        // Roles first (creates user_clinic rows), then disciplines via onAfterSave
        await roleAssignmentRef.current?.save();
      } finally {
        setIsSaving(false);
      }
    }, [canEditUserInfo]);

    return (
      <Dialog
        open={!!member}
        onOpenChange={open => {
          if (!open) onClose();
        }}
      >
        <DialogContent className='flex max-h-[88vh] min-h-0 flex-col sm:max-w-[740px]'>
          <DialogHeader className='pb-0'>
            <DialogTitle className='flex items-center gap-2'>
              <Users className='h-5 w-5 flex-shrink-0 text-muted-foreground' />
              <span className='min-w-0 break-words'>{dialogTitle}</span>
            </DialogTitle>
            <DialogDescription>{member?.email}</DialogDescription>
          </DialogHeader>

          <Separator />

          {/* One scroll region for every section: pinning employee type and
              the RogerDoc link left the roles list almost no height. */}
          <div className='-mr-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-2'>
            {isSuperUser && member?.roger_user_id && (
              <EditUserInfoSection
                ref={editUserInfoRef}
                userId={member.roger_user_id}
                firstName={member.first_name}
                lastName={member.last_name}
                isRogerEmployee={member.is_roger_employee ?? false}
              />
            )}

            {member?.roger_user_id && (
              <UserTypeSection
                ref={saveUserTypeRef}
                userId={member.roger_user_id}
                userType={member.user_type}
                showTopSeparator={isSuperUser}
              />
            )}

            <RoleAssignmentContent
              ref={roleAssignmentRef}
              assignment={roleAssignment}
              clinics={clinics}
              onClose={onClose}
              hideFooter
              onLoadingChange={setIsRolesLoading}
              clinicDisciplines={clinicDisciplines}
            />

            {member?.roger_user_id && (
              <EmployeeTypeSection
                rows={employeeTypeRows}
                invalidClinicIds={invalidEmployeeTypeClinicIds}
                onChangeType={setEmployeeType}
                onChangeOther={setEmployeeTypeOther}
              />
            )}

            {member && (
              <RogerDocAccountSection
                member={member}
                onChanged={handleRogerDocLinkChanged}
              />
            )}
          </div>

          <Separator />

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type='button'
              onClick={handleSave}
              disabled={isSaving || isRolesLoading}
            >
              {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  });
